import React, { useState } from 'react';
import { Form, Input, Button, Card, Select, DatePicker, message, Typography } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import api from '../../api/axios';
import { useNavigate } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const Register = () => {
    const [current, setCurrent] = useState(0);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const next = async () => {
        try {
            await form.validateFields(); 
            setCurrent(current + 1);
        } catch (info) { console.log('Validate Failed:', info); }
    };

    const prev = () => setCurrent(current - 1);

    const onFinish = async () => {
    setLoading(true);
    try {
        // 1. Force validation of ALL fields across all steps
        await form.validateFields();
        
        // 2. Get ALL values preserved in the form store
        const allValues = form.getFieldsValue(true);
        
        const payload = { 
            ...allValues, 
            dob: allValues.dob?.format('YYYY-MM-DD') 
        };
        
        console.log("Final Payload being sent:", payload); // Debug to see the full object

        await api.post('/register', payload);
        message.success('Registration successful!');
        navigate('/login');
    } catch (error) {
        if (error.errorFields) {
            message.error('Please complete all required fields in all steps');
        } else {
            message.error(error.response?.data?.message || 'Registration failed');
        }
    } finally {
        setLoading(false);
    }
};

    // Styling helpers based on your inspiration image
    const darkThemeStyles = {
        container: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#0D0D0D', padding: '20px' },
        card: { width: 800, background: '#141414', borderRadius: '16px', border: '1px solid #333', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' },
        input: { background: 'transparent', border: '1px solid #444', color: '#fff', borderRadius: '8px', padding: '10px' },
        progressSegment: (idx) => ({
            flex: 1, height: '4px', borderRadius: '2px', background: current >= idx ? '#FE1F08' : '#333', 
            transition: 'background 0.3s ease', boxShadow: current >= idx ? '0 0 8px #FE1F08' : 'none'
        })
    };

    return (
        <div style={darkThemeStyles.container}>
            <Card style={darkThemeStyles.card} bodyStyle={{ padding: '40px' }}>
                <div style={{ textAlign: 'center', marginBottom: 40 }}>
                    <img src="/assets/images/logo.png" alt="Logo" style={{ width: 50, marginBottom: 16 }} />
                    <Title level={2} style={{ color: '#fff', margin: 0 }}>Create Account</Title>
                    <Paragraph style={{ color: '#888' }}>Start your journey with Chalom Hights.</Paragraph>
                    
                    {/* CUSTOM PROGRESS BARS (From Inspiration) */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: 30 }}>
                        <div style={darkThemeStyles.progressSegment(0)} />
                        <div style={darkThemeStyles.progressSegment(1)} />
                        <div style={darkThemeStyles.progressSegment(2)} />
                    </div>
                </div>

                <Form form={form} layout="vertical" onFinish={onFinish} preserve={true}>
                    {/* STEP 1: ACCOUNT */}
                    {current === 0 && (
                        <div className="fade-in">
                            <Form.Item name="role" label={<Text style={{color: '#888'}}>I am a...</Text>} rules={[{ required: true }]}>
                                <Select 
                                    placeholder="Select Role" 
                                    // Force the dropdown menu to have a dark background
                                    dropdownStyle={{ 
                                        backgroundColor: '#141414', 
                                        border: '1px solid #333',
                                        borderRadius: '8px',
                                        padding: '4px'
                                    }}
                                    // Add a class for global CSS targeting
                                    popupClassName="dark-select-dropdown"
                                    style={{ width: '100%' }}
                                >
                                    <Select.Option value="mentee">Mentee</Select.Option>
                                    <Select.Option value="mentor">Mentor</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item name="name" label={<Text style={{color: '#888'}}>Full Name</Text>} rules={[{ required: true }]}>
                                <Input style={darkThemeStyles.input} />
                            </Form.Item>
                            <Form.Item name="email" label={<Text style={{color: '#888'}}>Email</Text>} rules={[{ required: true, type: 'email' }]}>
                                <Input style={darkThemeStyles.input} />
                            </Form.Item>
                            <Form.Item name="password" label={<Text style={{color: '#888'}}>Password</Text>} rules={[{ required: true, min: 8 }]}>
                                <Input.Password style={darkThemeStyles.input} />
                            </Form.Item>
                        </div>
                    )}

                    {/* STEP 2: PERSONAL */}
                    {current === 1 && (
                        <div className="fade-in">
                            <Form.Item name="title" label={<Text style={{color: '#888'}}>Title (Mr/Mrs/Dr)</Text>} rules={[{ required: true }]}>
                                <Select 
                                    placeholder="Select Title" 
                                    dropdownStyle={{ 
                                        backgroundColor: '#141414', 
                                        border: '1px solid #333',
                                        borderRadius: '8px',
                                        padding: '4px'
                                    }}
                                    popupClassName="dark-select-dropdown"
                                    style={{ width: '100%' }}
                                >
                                    <Select.Option value="Mr">Mr</Select.Option>
                                    <Select.Option value="Mrs">Mrs</Select.Option>
                                    <Select.Option value="Dr">Dr</Select.Option>
                                    <Select.Option value="Ms">Ms</Select.Option>
                                    <Select.Option value="Miss">Miss</Select.Option>
                                    <Select.Option value="Prof">Professor</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item name="dob" label={<Text style={{color: '#888'}}>Date of Birth</Text>} rules={[{ required: true }]}>
                                <DatePicker style={{ ...darkThemeStyles.input, width: '100%' }} />
                            </Form.Item>
                            <Form.Item name="phone_number" label={<Text style={{color: '#888'}}>Phone Number</Text>} rules={[{ required: true }]}>
                                <Input style={darkThemeStyles.input} />
                            </Form.Item>
                        </div>
                    )}

                    {/* STEP 3: PROFILE */}
                    {current === 2 && (
                        <div className="fade-in">
                            <Form.Item name="profession" label={<Text style={{color: '#888'}}>Profession</Text>} rules={[{ required: true }]}>
                                <Input style={darkThemeStyles.input} />
                            </Form.Item>
                            <Form.Item name="bio" label={<Text style={{color: '#888'}}>Short Bio</Text>} rules={[{ required: true }]}>
                                <TextArea rows={4} style={darkThemeStyles.input} />
                            </Form.Item>
                        </div>
                    )}
                    
                    <div style={{ marginTop: 40, display: 'flex', gap: '16px' }}>
                        {current > 0 && (
                            <Button 
                                icon={<LeftOutlined />} 
                                onClick={prev} 
                                style={{ background: '#222', color: '#fff', border: 'none', height: '45px' }}
                            />
                        )}
                        
                        <Button 
                            type="primary" 
                            onClick={current < 2 ? next : form.submit} 
                            loading={loading}
                            block
                            style={{ background: '#fff', color: '#000', border: 'none', height: '45px', fontWeight: 'bold' }}
                        >
                            {current < 2 ? 'Next step' : 'Create account'}
                        </Button>
                    </div>
                </Form>
                <div style={{ textAlign: 'center', marginTop: 24, color: '#888' }}>
                    Already have an account? <a href="/login" style={{ color: '#FE1F08' }}>Login Now</a>
                </div>
            </Card>
        </div>
    );
};

export default Register;